
import React from 'react';
import { ApparelProduct } from '@/types/models';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X } from 'lucide-react';
import { formatCurrency } from '@/lib/format';

interface ProductListProps {
  selectedProducts: Array<{
    product: ApparelProduct;
    quantity: number;
    size?: string;
  }>;
  onRemoveProduct: (index: number) => void;
  onUpdateQuantity: (index: number, quantity: number) => void;
  onUpdateSize: (index: number, size: string) => void;
}

const ProductList: React.FC<ProductListProps> = ({
  selectedProducts,
  onRemoveProduct,
  onUpdateQuantity,
  onUpdateSize,
}) => {
  const calculateSubtotal = () => {
    return selectedProducts.reduce((sum, item) => {
      return sum + (item.product.price_cents * item.quantity);
    }, 0);
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <h3 className="text-lg font-medium mb-4">Selected Items</h3>
        
        {selectedProducts.length === 0 ? (
          <p className="text-muted-foreground">No products selected</p>
        ) : (
          <div className="space-y-4">
            {selectedProducts.map((item, index) => (
              <div key={`${item.product.id}-${index}`} className="flex items-center justify-between border-b pb-3">
                <div className="flex items-center space-x-4">
                  {item.product.image_url && (
                    <img 
                      src={item.product.image_url} 
                      alt={item.product.name} 
                      className="w-16 h-16 object-cover rounded-md"
                    />
                  )}
                  <div>
                    <h4 className="font-medium">{item.product.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {formatCurrency(item.product.price_cents / 100)}
                    </p>
                    
                    <div className="flex items-center mt-2 space-x-2">
                      <label className="text-sm">Qty:</label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => onUpdateQuantity(index, parseInt(e.target.value) || 1)}
                        min="1"
                        className="w-16 h-8 text-sm"
                      />
                      
                      {item.product.requires_size && (
                        <div className="flex items-center space-x-2 ml-2">
                          <label className="text-sm">Size:</label>
                          <Select
                            value={item.size || ""}
                            onValueChange={(size) => onUpdateSize(index, size)}
                          >
                            <SelectTrigger className="w-20 h-8">
                              <SelectValue placeholder="Size" />
                            </SelectTrigger>
                            <SelectContent>
                              {item.product.sizes && Array.isArray(item.product.sizes) ? (
                                item.product.sizes.map((size: string) => (
                                  <SelectItem key={size} value={size}>
                                    {size}
                                  </SelectItem>
                                ))
                              ) : item.product.sizes ? (
                                Object.entries(item.product.sizes as Record<string, string>).map(([key, label]) => (
                                  <SelectItem key={key} value={key}>
                                    {label}
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem value="one-size">One Size</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <p className="font-medium">
                    {formatCurrency((item.product.price_cents * item.quantity) / 100)}
                  </p>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => onRemoveProduct(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            <div className="flex justify-end">
              <p className="font-semibold text-lg">
                Total: {formatCurrency(calculateSubtotal() / 100)}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductList;
